import { Component, ElementRef, HostListener, OnDestroy, ViewChild } from "@angular/core";
import { Subject, combineLatest } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { PublishStateService } from "app/js/src/infrastructure/clipping-studio/publish-state.service";
import { ClippingStudioManagerService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-manager.service";
import { SocialAccount } from "app/js/src/domain/clipping-studio/publish/publish-models";
import {
  CLIP_PREVIEW_EDITOR_SOURCE_JPEG_QUALITY,
  CLIP_PREVIEW_EDITOR_SOURCE_MAX_HEIGHT,
  CLIP_PREVIEW_PERSISTED_JPEG_QUALITY,
  CLIP_PREVIEW_PERSISTED_MAX_HEIGHT,
  encodeClipPreviewEditorSourceDataUrl,
  getAspectRatioWidthOverHeight,
  renderEditorCropToDataUrl,
} from "app/js/src/domain/clipping-studio/clip-preview-image-encode";

type ThumbnailAspectRatio = "16:9" | "9:16" | "1:1" | "4:5" | "4:3";

const CLIP_PREVIEW_UPLOAD_MAX_BYTES = 8 * 1024 * 1024;

@Component({
  selector: "evrideo-clipping-studio-thumbnail-tab",
  templateUrl: "./clipping-studio-thumbnail-tab.component.html",
  styleUrls: ["./clipping-studio-thumbnail-tab.component.scss"],
})
export class ClippingStudioThumbnailTabComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  @ViewChild("editorCanvasRef") private editorCanvasRef?: ElementRef<HTMLElement>;
  @ViewChild("editorImageRef") private editorImageRef?: ElementRef<HTMLImageElement>;
  @ViewChild("clipPreviewFileInput") private clipPreviewFileInput?: ElementRef<HTMLInputElement>;

  selectedDestinationId: string | null = null;
  clipPreviewDisplayUrl: string | null = null;
  currentAspectRatio: ThumbnailAspectRatio = "16:9";
  isCapturingSnapshot = false;

  showEditor = false;
  showImagePreview = false;
  isEditorClosing = false;
  isEditorDragging = false;
  editorActiveRatio: ThumbnailAspectRatio = "16:9";
  editorImageUrl: string | null = null;
  editorZoom = 100;
  editorOffsetX = 0;
  editorOffsetY = 0;
  editorImageFitMode: "width" | "height" = "width";
  private editorUploadedImage: string | null = null;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragOriginX = 0;
  private dragOriginY = 0;

  constructor(
    public publishState: PublishStateService,
    private manager: ClippingStudioManagerService,
    private toastr: ToastrService,
  ) {
    combineLatest([
      this.publishState.publishModel$,
      this.publishState.projectClipPreview$,
      this.publishState.configs$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshThumbnailDisplay();
      });

    this.manager.isCapturingClipPreview$
      .pipe(takeUntil(this.destroy$))
      .subscribe((capturing) => {
        this.isCapturingSnapshot = capturing;
      });
  }

  get hasSpecificDestination(): boolean {
    return !!this.selectedDestinationId;
  }

  get selectedDestination(): SocialAccount | null {
    if (!this.selectedDestinationId) return null;
    return this.getAccount(this.selectedDestinationId) ?? null;
  }

  get selectedDestinationLabel(): string {
    return this.selectedDestination?.name ?? "";
  }

  get isSelectedDestinationTikTok(): boolean {
    return this.selectedDestination?.type === "tt";
  }

  get isSelectedDestinationInstagram(): boolean {
    return this.selectedDestination?.type === "ig";
  }

  get isTikTokSourceSnapshotDisabled(): boolean {
    return this.isSelectedDestinationTikTok && this.publishState.currentEditorMode === "SOURCE";
  }

  get editorImageTransform(): string {
    return `translate(${this.editorOffsetX}px, ${this.editorOffsetY}px) scale(${this.editorZoom / 100})`;
  }

  ngOnDestroy(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDestinationDropdownChange(value: string | null): void {
    this.selectedDestinationId = value?.trim() || null;
    this.refreshThumbnailDisplay();
  }

  @HostListener("document:mousemove", ["$event"])
  onDocumentMouseMove(event: MouseEvent): void {
    if (!this.isEditorDragging) return;
    const nextX = this.dragOriginX + (event.clientX - this.dragStartX);
    const nextY = this.dragOriginY + (event.clientY - this.dragStartY);
    this.applyClampedEditorOffset(nextX, nextY);
  }

  @HostListener("document:mouseup")
  onDocumentMouseUp(): void {
    this.stopEditorImageDrag();
  }

  onTakeSnapshot(): void {
    if (this.isTikTokSourceSnapshotDisabled || this.isCapturingSnapshot) return;

    this.publishState.requestClipPreviewSnapshot(
      this.currentAspectRatio,
      this.selectedDestinationId,
    );
  }

  openClipPreviewUpload(): void {
    if (this.isCapturingSnapshot) return;
    this.clipPreviewFileInput?.nativeElement?.click();
  }

  onClipPreviewFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      this.toastr.warning("Please choose an image file (PNG, JPEG, WebP, or GIF).");
      return;
    }
    if (file.size > CLIP_PREVIEW_UPLOAD_MAX_BYTES) {
      this.toastr.error("Image is too large. Maximum size is 8 MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      void this.onClipPreviewFileLoaded(reader.result as string);
    };
    reader.onerror = () => {
      this.toastr.error("Could not read the file.");
    };
    reader.readAsDataURL(file);
  }

  async openEditor(): Promise<void> {
    if (this.isSelectedDestinationTikTok || this.isCapturingSnapshot) return;

    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    const resolved = await this.publishState.resolveClipPreviewForEditor(
      this.selectedDestinationId,
    );
    this.isEditorClosing = false;
    this.isEditorDragging = false;
    this.editorActiveRatio = resolved.editorActiveRatio;
    this.editorUploadedImage = resolved.uploadedImage;
    this.editorImageUrl = resolved.editorImageUrl;
    this.editorImageFitMode = "width";
    this.editorZoom = resolved.editor.zoom;
    this.editorOffsetX = resolved.editor.offsetX;
    this.editorOffsetY = resolved.editor.offsetY;
    this.showEditor = true;
  }

  openImagePreview(): void {
    if (!this.clipPreviewDisplayUrl) return;
    this.showImagePreview = true;
  }

  closeImagePreview(): void {
    this.showImagePreview = false;
  }

  closeEditor(): void {
    if (this.isEditorClosing) return;
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    this.isEditorClosing = true;
    this.isEditorDragging = false;
    this.closeTimer = setTimeout(() => {
      this.showEditor = false;
      this.editorImageUrl = null;
      this.isEditorClosing = false;
      this.closeTimer = null;
    }, 180);
  }

  resetEditorCrop(): void {
    this.editorZoom = 100;
    this.editorOffsetX = 0;
    this.editorOffsetY = 0;
  }

  onEditorZoomChange(value: number): void {
    this.editorZoom = value;
    this.applyClampedEditorOffset(this.editorOffsetX, this.editorOffsetY);
  }

  onEditorImageLoaded(event: Event): void {
    const image = event.target as HTMLImageElement;
    const sourceRatio = image?.naturalWidth && image?.naturalHeight
      ? image.naturalWidth / image.naturalHeight
      : getAspectRatioWidthOverHeight(this.editorActiveRatio);
    const targetRatio = getAspectRatioWidthOverHeight(this.editorActiveRatio);
    this.editorImageFitMode = sourceRatio < targetRatio ? "width" : "height";
    this.applyClampedEditorOffset(this.editorOffsetX, this.editorOffsetY);
  }

  startEditorImageDrag(event: MouseEvent): void {
    if (!this.editorImageUrl || event.button !== 0) return;
    this.isEditorDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragOriginX = this.editorOffsetX;
    this.dragOriginY = this.editorOffsetY;
    event.preventDefault();
  }

  stopEditorImageDrag(): void {
    this.isEditorDragging = false;
  }

  onEditorCancel(): void {
    this.closeEditor();
  }

  async onEditorApply(): Promise<void> {
    try {
      const outputDataUrl = await this.renderEditorImageToDataUrl(
        CLIP_PREVIEW_PERSISTED_MAX_HEIGHT,
        CLIP_PREVIEW_PERSISTED_JPEG_QUALITY,
      );
      if (!outputDataUrl) {
        this.toastr.warning("No image to apply.");
        return;
      }
      await this.publishState.applyClipPreviewFromEditor(this.selectedDestinationId, {
        previewBase64: outputDataUrl,
        aspectRatio: this.editorActiveRatio,
        editor: {
          zoom: this.editorZoom,
          offsetX: this.editorOffsetX,
          offsetY: this.editorOffsetY,
        },
        uploadedImage: this.editorUploadedImage,
      });
      this.refreshThumbnailDisplay();
      this.toastr.success("Thumbnail applied.");
      this.closeEditor();
    } catch {
      this.toastr.error("Could not apply thumbnail changes.");
    }
  }

  async onEditorDownload(): Promise<void> {
    try {
      const outputDataUrl = await this.renderEditorImageToDataUrl(
        CLIP_PREVIEW_EDITOR_SOURCE_MAX_HEIGHT,
        CLIP_PREVIEW_EDITOR_SOURCE_JPEG_QUALITY,
      );
      if (!outputDataUrl) {
        this.toastr.warning("No image to download.");
        return;
      }
      const link = document.createElement("a");
      link.href = outputDataUrl;
      link.download = `thumbnail-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      this.toastr.error("Could not download thumbnail.");
    }
  }

  private async onClipPreviewFileLoaded(dataUrl: string | null | undefined): Promise<void> {
    if (!dataUrl?.startsWith("data:image")) {
      this.toastr.error("Could not read the image.");
      return;
    }
    const editorSource = await encodeClipPreviewEditorSourceDataUrl(dataUrl);
    if (!editorSource) {
      this.toastr.error("Could not prepare the image for editing.");
      return;
    }
    this.editorImageUrl = editorSource;
    this.editorImageFitMode = "width";
    this.editorUploadedImage = editorSource;
    this.resetEditorCrop();
    this.toastr.success("Image loaded. Click Apply to save.");
  }

  private async renderEditorImageToDataUrl(
    maxOutputHeight: number,
    jpegQuality: number,
  ): Promise<string | null> {
    const sourceUrl = this.editorImageUrl;
    if (!sourceUrl) return null;

    const canvasRect = this.editorCanvasRef?.nativeElement.getBoundingClientRect();
    const naturalWidth = this.editorImageRef?.nativeElement?.naturalWidth ?? 0;
    const naturalHeight = this.editorImageRef?.nativeElement?.naturalHeight ?? 0;

    return renderEditorCropToDataUrl({
      sourceUrl,
      aspectRatio: this.editorActiveRatio,
      editorZoom: this.editorZoom,
      editorOffsetX: this.editorOffsetX,
      editorOffsetY: this.editorOffsetY,
      viewportWidth: Math.max(1, Math.round(canvasRect?.width ?? 0)),
      viewportHeight: Math.max(1, Math.round(canvasRect?.height ?? 0)),
      sourceNaturalWidth: naturalWidth,
      sourceNaturalHeight: naturalHeight,
      maxOutputHeight,
      jpegQuality,
    });
  }

  private applyClampedEditorOffset(nextX: number, nextY: number): void {
    const canvas = this.editorCanvasRef?.nativeElement;
    if (!canvas) {
      this.editorOffsetX = nextX;
      this.editorOffsetY = nextY;
      return;
    }
    const rect = canvas.getBoundingClientRect();
    const zoomScale = this.editorZoom / 100;
    const { width: baseWidth, height: baseHeight } = this.getBaseEditorDrawSize(rect.width, rect.height);
    const maxOffsetX = Math.max(0, ((baseWidth * zoomScale) - rect.width) / 2);
    const maxOffsetY = Math.max(0, ((baseHeight * zoomScale) - rect.height) / 2);
    this.editorOffsetX = Math.min(maxOffsetX, Math.max(-maxOffsetX, nextX));
    this.editorOffsetY = Math.min(maxOffsetY, Math.max(-maxOffsetY, nextY));
  }

  private getBaseEditorDrawSize(canvasWidth: number, canvasHeight: number): { width: number; height: number } {
    const naturalWidth = this.editorImageRef?.nativeElement?.naturalWidth ?? 0;
    const naturalHeight = this.editorImageRef?.nativeElement?.naturalHeight ?? 0;
    if (!naturalWidth || !naturalHeight) {
      return { width: canvasWidth, height: canvasHeight };
    }

    const baseScale = Math.max(canvasWidth / naturalWidth, canvasHeight / naturalHeight);
    return {
      width: naturalWidth * baseScale,
      height: naturalHeight * baseScale,
    };
  }

  previewAspectRatioClass(ratio: ThumbnailAspectRatio): string {
    switch (ratio) {
      case "9:16":
        return "thumbnail-tab__preview--9-16";
      case "1:1":
        return "thumbnail-tab__preview--1-1";
      case "4:5":
        return "thumbnail-tab__preview--4-5";
      case "4:3":
        return "thumbnail-tab__preview--4-3";
      case "16:9":
      default:
        return "thumbnail-tab__preview--16-9";
    }
  }

  editorAspectRatioClass(ratio: ThumbnailAspectRatio): string {
    switch (ratio) {
      case "9:16":
        return "thumbnail-editor__canvas--9-16";
      case "1:1":
        return "thumbnail-editor__canvas--1-1";
      case "4:5":
        return "thumbnail-editor__canvas--4-5";
      case "4:3":
        return "thumbnail-editor__canvas--4-3";
      case "16:9":
      default:
        return "thumbnail-editor__canvas--16-9";
    }
  }

  editorDialogClass(ratio: ThumbnailAspectRatio): string {
    switch (ratio) {
      case "9:16":
        return "thumbnail-editor__dialog--ratio-9-16";
      case "1:1":
        return "thumbnail-editor__dialog--ratio-1-1";
      case "4:5":
        return "thumbnail-editor__dialog--ratio-4-5";
      case "4:3":
        return "thumbnail-editor__dialog--ratio-4-3";
      case "16:9":
      default:
        return "";
    }
  }

  private refreshThumbnailDisplay(): void {
    this.clipPreviewDisplayUrl = this.publishState.getThumbnailDisplayUrl(
      this.selectedDestinationId,
    );
    this.currentAspectRatio = this.resolveCurrentAspectRatio();
  }

  private resolveCurrentAspectRatio(): ThumbnailAspectRatio {
    if (!this.selectedDestinationId) return "16:9";
    const destination = this.getAccount(this.selectedDestinationId);
    if (!destination) return "16:9";
    return this.normalizeRatio(this.publishState.getConfig(destination.id).aspectRatio);
  }

  private getAccount(id: string): SocialAccount | undefined {
    return this.publishState.getDestinations().find((account) => account.id === id);
  }

  private normalizeRatio(ratio: string): ThumbnailAspectRatio {
    if (ratio === "9:16" || ratio === "1:1" || ratio === "4:5" || ratio === "4:3") return ratio;
    return "16:9";
  }
}
